/**
 * Logger utility that automatically disables console logging in production
 *
 * Usage:
 *   import { logger } from '@/models/logger'
 *   logger.log('message')
 *   logger.info('info message')
 *   logger.warn('warning message')
 *   logger.error('error message')
 *   logger.debug('debug message')
 *
 * Backend logging (enable from browser console):
 *   __logger.enableBackend()     // Start sending logs to backend
 *   __logger.disableBackend()    // Stop sending logs
 *   __logger.setUrl('http://localhost:3333')  // Use local log server
 *   __logger.backend('context', 'message', data)  // Explicit backend log
 *   __logger.flush()             // Force send buffered logs
 */

import axios from 'axios'

interface BackendLogEntry {
  timestamp: string
  level: string
  context: string
  message: string
  data?: any
  url: string
}

class Logger {
  private isProduction: boolean
  private enabledInProduction: Set<string>
  private backendEnabled: boolean = false
  private logBuffer: BackendLogEntry[] = []
  private flushInterval: number | null = null
  private sessionId: string
  private customLogUrl: string | null = null
  private consecutiveFailures: number = 0
  private readonly maxConsecutiveFailures: number = 3

  constructor() {
    this.isProduction = process.env.NODE_ENV === 'production'
    this.enabledInProduction = new Set(['error', 'warn'])
    this.sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

    // Auto-enable for dev testing only
    if (!this.isProduction) {
      this.customLogUrl = 'http://localhost:3333'
      this.enableBackend()
    }
  }

  /**
   * Set a custom log server URL (e.g., 'http://localhost:3333')
   */
  setUrl(url: string | null): void {
    this.customLogUrl = url
    console.info(`[Logger] Log URL set to: ${url ?? 'default'}`)
  }

  /**
   * Enable backend logging - logs will be sent to server
   */
  enableBackend(): void {
    this.backendEnabled = true
    this.consecutiveFailures = 0 // Reset failure count on re-enable
    if (!this.flushInterval) {
      this.flushInterval = window.setInterval(() => this.flush(), 5000)
    }
    this.addToBuffer('info', 'logger', 'Backend logging enabled', { sessionId: this.sessionId })
    console.info('[Logger] Backend logging enabled')
  }

  /**
   * Disable backend logging
   */
  disableBackend(): void {
    this.flush()
    this.backendEnabled = false
    if (this.flushInterval) {
      clearInterval(this.flushInterval)
      this.flushInterval = null
    }
    console.info('[Logger] Backend logging disabled')
  }

  /**
   * Check if backend logging is enabled
   */
  isBackendEnabled(): boolean {
    return this.backendEnabled
  }

  /**
   * Explicit backend log - use for specific debugging
   */
  backend(context: string, message: string, data?: any): void {
    if (!this.isProduction) {
      console.log(`[${context}]`, message, data ?? '')
    }
    this.addToBuffer('debug', context, message, data)
  }

  private addToBuffer(level: string, context: string, message: string, data?: any): void {
    if (!this.backendEnabled) return

    this.logBuffer.push({
      timestamp: new Date().toISOString(),
      level,
      context,
      message: typeof message === 'string' ? message : JSON.stringify(message),
      data: data !== undefined ? this.sanitize(data) : undefined,
      url: window.location.href
    })

    if (this.logBuffer.length >= 50) {
      this.flush()
    }
  }

  private sanitize(data: any, depth = 0): any {
    if (depth > 5) return '[max depth]'
    if (data === null || data === undefined) return data
    if (typeof data !== 'object') return data

    if (Array.isArray(data)) {
      return data.slice(0, 50).map((item) => this.sanitize(item, depth + 1))
    }

    const result: Record<string, any> = {}
    for (const key of Object.keys(data).slice(0, 30)) {
      const lower = key.toLowerCase()
      if (lower.includes('token') || lower.includes('password') || lower.includes('secret')) {
        result[key] = '[REDACTED]'
      } else {
        try {
          result[key] = this.sanitize(data[key], depth + 1)
        } catch {
          result[key] = '[error]'
        }
      }
    }
    return result
  }

  /**
   * Flush buffered logs to backend
   */
  async flush(): Promise<void> {
    if (this.logBuffer.length === 0) return

    const logs = [...this.logBuffer]
    this.logBuffer = []

    const baseUrl = this.customLogUrl ?? import.meta.env.VITE_API_URL
    if (!baseUrl) return

    try {
      const playerToken = document.cookie
        .split('; ')
        .find((c) => c.startsWith('playerToken='))
        ?.split('=')[1]

      await axios.post(
        `${baseUrl.replace(/\/$/, '')}/debug/log`,
        { logs, sessionId: this.sessionId },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(playerToken && { 'X-Auth-Token': playerToken })
          }
        }
      )
      // Reset failure count on success
      this.consecutiveFailures = 0
    } catch {
      this.consecutiveFailures++

      if (this.consecutiveFailures >= this.maxConsecutiveFailures) {
        console.warn(`[Logger] Backend unreachable after ${this.maxConsecutiveFailures} attempts, disabling.`)
        this.backendEnabled = false
        if (this.flushInterval) {
          clearInterval(this.flushInterval)
          this.flushInterval = null
        }
        this.logBuffer = [] // Clear buffer since we're giving up
      }
    }
  }

  /**
   * Standard log - disabled in production
   */
  log(...args: any[]): void {
    if (!this.isProduction) {
      console.log(...args)
    }
    if (this.backendEnabled) {
      this.addToBuffer('log', 'general', args[0], args.slice(1))
    }
  }

  /**
   * Info log - disabled in production
   */
  info(...args: any[]): void {
    if (!this.isProduction) {
      console.info(...args)
    }
    if (this.backendEnabled) {
      this.addToBuffer('info', 'general', args[0], args.slice(1))
    }
  }

  /**
   * Debug log - disabled in production
   */
  debug(...args: any[]): void {
    if (!this.isProduction) {
      console.debug(...args)
    }
    if (this.backendEnabled) {
      this.addToBuffer('debug', 'general', args[0], args.slice(1))
    }
  }

  /**
   * Warning log - enabled in production
   */
  warn(...args: any[]): void {
    console.warn(...args)
    if (this.backendEnabled) {
      this.addToBuffer('warn', 'general', args[0], args.slice(1))
    }
  }

  /**
   * Error log - enabled in production
   */
  error(...args: any[]): void {
    console.error(...args)
    if (this.backendEnabled) {
      this.addToBuffer('error', 'general', args[0], args.slice(1))
    }
  }

  /**
   * Table log - disabled in production
   */
  table(data: any): void {
    if (!this.isProduction) {
      console.table(data)
    }
    if (this.backendEnabled) {
      this.addToBuffer('table', 'general', 'table', data)
    }
  }

  /**
   * Group log - disabled in production
   */
  group(...args: any[]): void {
    if (!this.isProduction) {
      console.group(...args)
    }
  }

  /**
   * Group end - disabled in production
   */
  groupEnd(): void {
    if (!this.isProduction) {
      console.groupEnd()
    }
  }

  getSessionId(): string {
    return this.sessionId
  }
}

// Export singleton instance
export const logger = new Logger()

// Export default for convenience
export default logger

// Expose globally for browser console debugging
if (typeof window !== 'undefined') {
  ;(window as any).__logger = logger
}
